import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BadgeCheck, ExternalLink, FileText, Globe, Mail, MapPin, Pencil, Phone } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { EXPENSE_CATS } from "@/components/finance/ExpensesCard";
import { api, formatApiError } from "@/lib/api";
import { useFormat } from "@/lib/format";

const FIELDS = [["name", "Nom"], ["phone", "Téléphone"], ["email", "E-mail"], ["website", "Site internet"], ["address", "Adresse"], ["contract_number", "N° de contrat / client"]];
const href = (u) => (/^https?:\/\//.test(u) ? u : `https://${u}`);

const Line = ({ icon: Icon, label, children, official, testid }) => (
  <div className="flex items-start gap-3 py-2" data-testid={testid}>
    <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
    <div className="min-w-0 flex-1"><div className="text-xs text-muted-foreground">{label}{official && <span className="ml-1 inline-flex items-center gap-0.5 text-success"><BadgeCheck className="h-3 w-3" /> officiel</span>}</div>
      <div className="break-words text-sm font-medium">{children || <span className="font-normal text-muted-foreground">À compléter</span>}</div></div>
  </div>
);

const Details = ({ s }) => {
  const { money, date } = useFormat();
  return (
    <div className="divide-y divide-border">
      <Line icon={Phone} label="Téléphone" official={s.official?.includes("phone")} testid="supplier-phone">
        {s.phone && <a href={`tel:${s.phone.replace(/\s/g, "")}`} className="text-primary underline-offset-2 hover:underline">{s.phone}</a>}
        {s.phone_hours && <div className="text-xs font-normal text-muted-foreground">{s.phone_hours}</div>}
      </Line>
      <Line icon={Mail} label="E-mail" testid="supplier-email">{s.email && <a href={`mailto:${s.email}`} className="text-primary hover:underline">{s.email}</a>}</Line>
      <Line icon={Globe} label="Site internet" official={s.official?.includes("website")} testid="supplier-website">
        {s.website && <a href={href(s.website)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline">{s.website.replace(/^https?:\/\//, "")}<ExternalLink className="h-3 w-3" /></a>}
      </Line>
      <Line icon={MapPin} label="Adresse" testid="supplier-address">{s.address}</Line>
      <Line icon={FileText} label="N° de contrat / client" testid="supplier-contract">{s.contract_number}</Line>
      {s.notes && <p className="py-2 text-sm text-muted-foreground" data-testid="supplier-notes">{s.notes}</p>}
      {s.count > 0 && <p className="pt-3 text-xs text-muted-foreground" data-testid="supplier-stats">{s.count} opération(s) sur 6 mois · {money(s.spent_6m, { maximumFractionDigits: 0 })}{s.last_date && ` · dernière le ${date(s.last_date)}`}</p>}
    </div>
  );
};

const EditForm = ({ s, onDone, onCancel }) => {
  const [v, setV] = useState(() => Object.fromEntries([...FIELDS, ["notes"]].map(([k]) => [k, s[k] || ""])));
  const save = async (e) => {
    e.preventDefault();
    try {
      const body = Object.fromEntries(Object.entries(v).map(([k, x]) => [k, x.trim() || (k === "name" ? s.name : null)]));
      const { data } = s.isNew ? await api.post("/finance/suppliers", body) : await api.put(`/finance/suppliers/${s.key}`, body);
      toast.success("Fiche fournisseur enregistrée");
      onDone(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <form onSubmit={save} className="space-y-3" data-testid="supplier-edit-form">
      <div className="grid gap-3 sm:grid-cols-2">
        {FIELDS.map(([k, l]) => (
          <div key={k} className={k === "address" ? "sm:col-span-2" : ""}><Label className="text-xs">{l}</Label>
            <Input value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value })} required={k === "name"} type={k === "email" ? "email" : "text"} data-testid={`supplier-input-${k}`} /></div>
        ))}
      </div>
      <div><Label className="text-xs">Notes</Label><Textarea value={v.notes} onChange={(e) => setV({ ...v, notes: e.target.value })} rows={2} data-testid="supplier-input-notes" /></div>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" className="rounded-full" onClick={onCancel} data-testid="supplier-edit-cancel">Annuler</Button>
        <Button type="submit" className="rounded-full" data-testid="supplier-edit-save">Enregistrer</Button>
      </div>
    </form>
  );
};

export const SupplierDialog = ({ supplier, onClose }) => {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const list = useQuery({ queryKey: ["suppliers"], queryFn: () => api.get("/finance/suppliers").then((r) => r.data), enabled: !!supplier && !supplier.isNew });
  const s = supplier && (list.data?.find((x) => x.key === supplier.key) || supplier);
  useEffect(() => { setEditing(!!supplier?.isNew); }, [supplier]);
  const done = () => { ["suppliers", "overview"].forEach((k) => qc.invalidateQueries({ queryKey: [k] })); if (supplier.isNew) onClose(); else setEditing(false); };
  return (
    <Dialog open={!!supplier} onOpenChange={(o) => { if (!o) { onClose(); } }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="supplier-dialog">
        {s && (
          <>
            <DialogHeader>
              <DialogTitle data-testid="supplier-dialog-title">{s.isNew ? "Nouveau fournisseur" : s.name}</DialogTitle>
              <DialogDescription>{s.isNew ? "Ajoutez un fournisseur et ses coordonnées." : `${EXPENSE_CATS[s.category] || "Autre"} · ${s.source === "catalog" ? "coordonnées officielles du service client" : s.source === "manual" ? "fiche ajoutée par vous" : "reconnu depuis vos opérations"}`}</DialogDescription>
            </DialogHeader>
            {editing ? <EditForm s={s} onDone={done} onCancel={() => (s.isNew ? onClose() : setEditing(false))} /> : (
              <>
                <Details s={s} />
                <Button variant="outline" className="w-full rounded-full" onClick={() => setEditing(true)} data-testid="supplier-edit-button"><Pencil className="mr-2 h-4 w-4" /> Compléter / modifier</Button>
              </>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
